import { useEffect, useSyncExternalStore } from 'react';

import { LoadingState } from '../components/states/LoadingState';
import { getRestoreStatus, restoreSession, subscribeToRestore } from './restore';
import { useSession } from './useSession';

const RESTORING_LABEL = 'Retomando sua sessão…';

/**
 * Nothing renders until we know whether there is a session.
 *
 * The router is why this is a gate rather than a flag some component consults.
 * `RequireSession` answers "no session" by navigating to the login screen, and
 * it would be right to -- at the moment it asked. Half a second later the
 * renewal comes back and the person is bounced somewhere else, having watched a
 * login form they never needed. Holding the whole tree until the question has
 * an answer means the first thing rendered is the right thing, and the deep
 * link they arrived on is still in the address bar when it is.
 *
 * The wait is one request to our own backend. A spinner that says what it is
 * doing is the honest thing to show for it, and it is what a screen reader
 * hears too.
 *
 * Someone already signed in -- having just signed in, or a test that seeded a
 * session -- renders straight through on the first pass. There is nothing to
 * restore, and a spinner in front of a session we already hold would be a flash
 * of exactly the kind this component exists to remove.
 */
export function SessionBoot({ children }: { children: React.ReactNode }) {
  const status = useSyncExternalStore(subscribeToRestore, getRestoreStatus, getRestoreStatus);
  const session = useSession();

  useEffect(() => {
    // Once per page load, whatever happens to this component: `restoreSession`
    // memoises the attempt, so a remount -- StrictMode's deliberate double
    // mount above all -- does not spend a second refresh token. The server
    // reads the second use of a rotated token as a stolen cookie and revokes
    // the session, so a double renewal would sign the person out on every
    // reload.
    void restoreSession();
  }, []);

  if (session === null && status === 'pending') {
    return (
      <div className="page page--centred">
        <LoadingState label={RESTORING_LABEL} />
      </div>
    );
  }

  return <>{children}</>;
}
